import { useMutation, useQueryClient } from 'react-query';
import { notificationsAxiosApi } from 'src/Config/axiosApi';
import {
  TRIAGE_MAX_IDS_PER_WRITE,
  TriageListResponse,
  TriageReadResponse,
} from '../types/notifications.types';
import { TRIAGE_INDEX_KEY, TRIAGE_LIST_KEY_ROOT } from './triageKeys';

/**
 * Clear the cards behind ONE row's mark, from the opener table or Descalces.
 *
 * Two steps, and the first is the point: the mark is painted from the INDEX,
 * which carries counts and no ids, and the write endpoint takes explicit ids
 * with no bulk-by-query — deliberately, so nothing the operator never saw can
 * be silenced. So this asks the server which cards are behind that mark, then
 * posts exactly those.
 *
 * 🚨 `retired: 'exclude'` on the read, matching `useTriageIndex`. The chip
 * counts the SCOPED set, so the button has to clear the scoped set: widening
 * here would clear cards on finished markets that the number the operator
 * clicked never included.
 *
 * 🚨 It lives in its own module, importing only types and the dependency-free
 * keys, and NOT in `useTriageList` beside its siblings. `EntryTabByPath` and
 * `DescalceCells` are its callers, and their suites replace `useTriageIndex`
 * with a one-export `vi.mock`; reaching it through `useTriageList` pulled that
 * module into their graph and four suites stopped COLLECTING — 91 tests
 * silently not run, which a green summary hides.
 *
 * Returns the ids it cleared, so a caller can offer an undo with them.
 */
export function useMarkTriageRowRead() {
  const qc = useQueryClient();
  return useMutation<
    { ids: string[]; res: TriageReadResponse | null },
    unknown,
    {
      ruleId?: string | null;
      pairId?: string | null;
      confirm?: (n: number) => boolean;
    }
  >(
    async ({ ruleId, pairId, confirm }) => {
      if (!ruleId && !pairId) return { ids: [], res: null };
      const { data } = await notificationsAxiosApi.get<TriageListResponse>(
        '/notifications/triage',
        {
          params: {
            state: 'unread',
            limit: TRIAGE_MAX_IDS_PER_WRITE,
            retired: 'exclude',
            ...(ruleId ? { ruleId } : {}),
            ...(pairId ? { pairId } : {}),
          },
        },
      );
      // A NARROWER guard than the tray's `isUsableTriageList`, and
      // deliberately so — not a second copy of it. The tray validates the
      // labels that say what its COUNTS mean, because it renders them; this
      // reads ids out of two arrays and renders no number at all, so "are the
      // two arrays there" is the whole contract it depends on.
      const buckets = [data?.counter?.items, data?.orphan?.items];
      if (!buckets.every(Array.isArray)) {
        throw new Error('triage response is missing a required bucket');
      }
      const ids = [...data.counter.items, ...data.orphan.items].map((i) => i.id);
      if (!ids.length) return { ids: [], res: null };
      if (ids.length > TRIAGE_MAX_IDS_PER_WRITE) {
        throw new Error(
          `refusing to post ${ids.length} ids — the endpoint accepts at most ${TRIAGE_MAX_IDS_PER_WRITE}`,
        );
      }
      // The count comes from the SERVER, not from the chip: between the paint
      // and the click another operator may have cleared some, and the number
      // in the confirm has to be what this click will actually silence.
      if (confirm && !confirm(ids.length)) return { ids: [], res: null };
      const { data: res } = await notificationsAxiosApi.post<TriageReadResponse>(
        '/notifications/read',
        { ids },
      );
      return { ids, res };
    },
    {
      mutationKey: 'triage:mark-row-read',
      onSuccess: ({ ids }) => {
        if (!ids.length) return;
        // Both, and by invalidation rather than a cache patch: the marks are
        // painted from the index, which no write touches, so without this the
        // chip the operator just cleared stays on the row until the next poll.
        void qc.invalidateQueries(TRIAGE_INDEX_KEY);
        void qc.invalidateQueries(TRIAGE_LIST_KEY_ROOT);
      },
    },
  );
}
